import React, { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { signInWithCustomToken, signOut, onAuthStateChanged, User, getIdTokenResult } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from './firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';

interface UserData {
  name: string;
  photo: any;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  isActive: boolean;
  companyId: string;
  departmentId: string;
  filterButtons?: string[];
}

interface DepartmentData {
  id: string;
  department: string;
  tabPermissions: {
    name: string;
    read: boolean;
    write: boolean;
  }[];
  actionPermission?: {
    name: string;
    allowed: boolean;
  }[];
  bulkActionPermission?: {
    name: string;
    id: number;
    active: boolean;
    view: boolean;
  }[];
  sidebarNavigation?: {
    id: number;
    name: string;
    normalIcon: string;
    activeIcon: string;
    link: string | object[]
    isActive: boolean;
    isVisible: boolean;
  }[];
  selectedFilterIds?: string[];
}

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  isSubAgent: boolean;
  isActive: boolean;
  userData: UserData | null;
  departmentData: DepartmentData | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isSubAgent, setIsSubAgent] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [departmentData, setDepartmentData] = useState<DepartmentData | null>(null);
  const navigate = useNavigate();

  const customToken = httpsCallable(functions, 'customToken');

  const login = async (email: string, password: string) => {
    try {
      const result = await customToken({ email, password });

      console.log("customToken result", result);

      const { token, user } = result.data as { token: string; user: any };

      // Store the custom token for Supabase
      localStorage.setItem('supabaseCustomToken', token);

      // Sign in with the custom token
      await signInWithCustomToken(auth, token);

      const userDocRef = doc(db, 'users', user.uid);

      const userDoc = await getDoc(userDocRef);


      if (!userDoc.exists()) {
        await signOut(auth);
        throw new Error('User not found.');
      }

      const userData = userDoc.data() as UserData;

      // set previous roles to there respective values
      prevRolesRef.current = {
        isAdmin: userData?.isAdmin || false,
        isManager: userData?.isManager || false,
        isStaff: userData?.isStaff || false,
      };
      const isUserAdmin = userData?.isAdmin || false;
      const isUserManager = userData?.isManager || false;
      const isUserStaff = userData?.isStaff || false;
      const isUserActive = userData?.isActive || false;

      // get departmentData using departmentId from userData
      const departmentDocRef = doc(db, 'companies', userData.companyId, 'departments', userData.departmentId);
      const departmentDoc = await getDoc(departmentDocRef);

      if (!isUserActive) {
        await signOut(auth);
        throw new Error('Your account is not active. Please contact administrator.');
      }

      localStorage.setItem('isAuthenticated', 'true');
      setIsAdmin(isUserAdmin);
      setIsManager(isUserManager);
      setIsStaff(isUserStaff);
      setIsActive(isUserActive);
      setUserData(userData);
      setDepartmentData(departmentDoc.data() as DepartmentData | null);

      if (isUserAdmin) {
        navigate('/admin/dashboard');
      } else if (isUserManager) {
        navigate('/manager/dashboard'); // Create this route for regular users
      } else {
        navigate('/staff/dashboard'); // Create this route for students
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific authentication errors
      if (error.code === 'functions/unauthenticated') {
        throw new Error('Invalid email or password');
      } else if (error.code === 'functions/not-found') {
        throw new Error('User not found');
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('User account has been disabled');
      } else if (error.code === 'functions/resource-exhausted') {
        throw new Error('Too many failed attempts. Please try again later');
      } else if (error.code === 'functions/internal') {
        throw new Error('Internal server error. Please try again');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Login failed. Please try again');
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);

      // Clear cookies and local storage
      document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });
      
      // Clear the Supabase custom token
      localStorage.removeItem('supabaseCustomToken');
      localStorage.clear();
      sessionStorage.clear();

      localStorage.removeItem('isAuthenticated');
      setIsAdmin(false);
      setIsManager(false);
      setIsStaff(false);
      setIsActive(false);
      setUserData(null);
      setDepartmentData(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if this is a student user
        const idTokenResult = await user.getIdTokenResult();
        if (idTokenResult.claims.role === 'student' || idTokenResult.claims.role === 'subAgent') {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        console.log("displayName", user.displayName);


        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data() as UserData;

          // Safe access to userData properties with optional chaining
          const companyId = userData?.companyId;
          const departmentId = userData?.departmentId;

          // Only try to get department data if both IDs exist
          let departmentData = null;
          if (companyId && departmentId) {
            const departmentDocRef = doc(db, 'companies', companyId, 'departments', departmentId);
            const departmentDoc = await getDoc(departmentDocRef);
            if (departmentDoc.exists()) {
              departmentData = departmentDoc.data() as DepartmentData;
            }
          }


          setCurrentUser(user);
          setIsAdmin(userData?.isAdmin || false);
          setIsManager(userData?.isManager || false);
          setIsStaff(userData?.isStaff || false);
          setIsActive(userData?.isActive || false);
          setUserData(userData);
          setDepartmentData(departmentData);

          if (!userData?.isActive) {
            await signOut(auth);
            setCurrentUser(null);
            setIsAdmin(false);
            setIsManager(false);
            setIsStaff(false);
            setIsActive(false);
            setUserData(null);
            setDepartmentData(null);
            navigate('/');
          } else if (window.location.pathname === '/' || window.location.pathname === '/student-login' || window.location.pathname === '/') {
            // Only redirect if on main login or student login page
            navigate(userData?.isAdmin ? '/admin/dashboard' : userData?.isManager ? '/manager/dashboard' : '/staff/dashboard');
          }
        } else {
          await signOut(auth);
          // Clear cookies and local storage
          document.cookie.split(";").forEach((c) => {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
          });
          localStorage.clear();
          sessionStorage.clear();
          setCurrentUser(null);
          setIsAdmin(false);
          setIsManager(false);
          setIsStaff(false);
          setIsActive(false);
          setUserData(null);
          setDepartmentData(null);
          navigate('/');
        }
        // }
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        setIsManager(false);
        setIsStaff(false);
        setIsActive(false);
        setUserData(null);
        setDepartmentData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const prevRolesRef = useRef<{
    isAdmin: boolean | null;
    isManager: boolean | null;
    isStaff: boolean | null;
  }>({
    isAdmin: null,
    isManager: null,
    isStaff: null,
  });

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Don't run if data is still loading or user is not authenticated
    if (loading || !currentUser) return;

    if (hasInitializedRef.current) {
      const prev = prevRolesRef.current;

      if (
        prev.isAdmin !== isAdmin ||
        prev.isManager !== isManager ||
        prev.isStaff !== isStaff
      ) {
        // Role has changed → sign out
        signOut(auth).then(() => {
          setCurrentUser(null);
          setIsAdmin(false);
          setIsManager(false);
          setIsStaff(false);
          setIsActive(false);
          setUserData(null);
          setDepartmentData(null);

          document.cookie.split(";").forEach((c) => {
            document.cookie = c
              .replace(/^ +/, "")
              .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
          });
          localStorage.clear();
          sessionStorage.clear();

          navigate('/');
        });
      }
    } else {
      hasInitializedRef.current = true; // Mark as initialized after first stable load
    }

    // Store current roles for future comparison
    prevRolesRef.current = { isAdmin, isManager, isStaff };
  }, [isAdmin, isManager, isStaff, currentUser, loading, navigate]);


  const value = {
    currentUser,
    login,
    logout,
    loading,
    isAdmin,
    isManager,
    isStaff,
    isSubAgent,
    isActive,
    userData,
    departmentData,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Updated ProtectedRoute component with role-based access
export const ProtectedRoute: React.FC<{
  children: ReactNode;
  requireAdmin?: boolean;
  requireManager?: boolean;
  requireStaff?: boolean;
}> = ({ children, requireAdmin = false, requireManager = false, requireStaff = false }) => {
  const { currentUser, loading, isAdmin, isManager, isStaff, isSubAgent, isActive } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }, [window.location.pathname]);

  useEffect(() => {
    if (!loading) {
      if (!currentUser || !isActive) {
        navigate('/');
      } else if (requireAdmin && !isAdmin && !isManager && isStaff) {
        navigate('/staff/dashboard');
      } else if (requireAdmin && !isAdmin && isManager && !isStaff) {
        navigate('/manager/dashboard');
      } else if (requireAdmin && !isAdmin && !isManager && !isStaff) {
        navigate('/');
      } else if (requireManager && isAdmin && !isManager && !isStaff) {
        navigate('/admin/dashboard');
      } else if (requireManager && !isAdmin && !isManager && isStaff) {
        navigate('/staff/dashboard');
      } else if (requireManager && !isAdmin && !isManager && !isStaff) {
        navigate('/');
      } else if (requireStaff && isAdmin && !isManager && !isStaff) {
        navigate('/admin/dashboard');
      } else if (requireStaff && !isAdmin && isManager && !isStaff) {
        navigate('/manager/dashboard');
      }
    }
  }, [currentUser, loading, isAdmin, isManager, isStaff, isActive, requireAdmin, navigate]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return currentUser && isActive && (!requireAdmin || isAdmin) ? <>{children}</> : null;
};